import {
	Alert,
	Box,
	Container,
	Heading,
	Spinner,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { ButtonLink, Breadcrumbs } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useFetchIntegration } from "../../../../Integrations"
import {
	AutomationTemplateCreator,
	TAutomationTemplateSummary,
} from "../../../../Automations"
import { generatePath, useNavigate, useParams } from "../../../../router-utils"

export default function WhatsAppIntegrationAutomationCreatePage() {
	const xhr = useXHR()
	const navigate = useNavigate()
	const { integrationId } = useParams(
		"/integrations/:integrationId/whatsapp/automations/new"
	)
	const {
		data: integration,
		error: integrationError,
		isValidating: integrationLoading,
	} = useFetchIntegration(integrationId)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [templates, setTemplates] = useState<Array<TAutomationTemplateSummary>>(
		[]
	)

	useEffect(() => {
		let mounted = true

		async function load() {
			if (!integration?.integrated_app_id) {
				return
			}

			setLoading(true)
			setError(null)

			try {
				const templateResponse = await xhr.get<{
					data: Array<TAutomationTemplateSummary>
				}>(
					`/integrations/whatsapp/${integration.integrated_app_id}/automation-templates`
				)

				if (!mounted) return

				setTemplates(templateResponse.data.data)
			} catch (nextError) {
				if (!mounted) return

				setError(
					(nextError as Error)?.message ||
						"Unable to load scoped WhatsApp automation templates."
				)
			} finally {
				if (mounted) {
					setLoading(false)
				}
			}
		}

		void load()

		return () => {
			mounted = false
		}
	}, [integration?.integrated_app_id, xhr])

	if (!integration) {
		if (integrationLoading) {
			return <Spinner padding="8" alignCenter />
		}

		return (
			<Alert status="warning">
				{integrationError?.message ||
					"Unable to load the WhatsApp integration."}
			</Alert>
		)
	}

	const automationsPath = generatePath(
		"/integrations/:integrationId/whatsapp/automations",
		{ integrationId }
	)

	return (
		<>
			<Helmet>
				<title>New WhatsApp Automation</title>
			</Helmet>
			<Breadcrumbs
				title="New WhatsApp Automation"
				items={[
					[generatePath("/org/integrations"), "Integrations"],
					[automationsPath, integration.name],
					["", "New WhatsApp Automation"],
				]}
			/>
			<Container paddingY="6" bgColor="default">
				{loading ? (
					<Spinner padding="8" alignCenter />
				) : error ? (
					<Stack gap="4">
						<Alert title="Unable to load templates" status="warning">
							{error}
						</Alert>
						<Box>
							<ButtonLink to={automationsPath} level="secondary">
								Back to Automations
							</ButtonLink>
						</Box>
					</Stack>
				) : (
					<AutomationTemplateCreator
						templates={templates}
						integratedAppId={integration.integrated_app_id || undefined}
						intro={
							<Stack gap="2">
								<Heading as="h2">Choose a WhatsApp Automation Template</Heading>
								<Text color="muted">
									Create a template-based automation using only sender numbers
									and approved WhatsApp templates connected to{" "}
									{integration.name}.
								</Text>
							</Stack>
						}
						allowCustom
						onCreated={(id) =>
							navigate("/automations/:automationId", {
								params: { automationId: String(id) },
								replace: true,
							})
						}
						onCancel={() =>
							navigate("/integrations/:integrationId/whatsapp/automations", {
								params: { integrationId },
								replace: true,
							})
						}
					/>
				)}
			</Container>
		</>
	)
}
